import React, { useContext, useEffect, useState } from 'react'

// Libs
import { uniq } from 'lodash'

// Components
import MainModal from './common/MainModal'
import { IconTeam } from '../icons/IconTeam'
import { EntriesSection } from './components/EntriesSection'
import { ScratchModalHeader } from './components/ScratchModalHeader'
import ViewsLoader from '../loader/ViewsLoader'

// Helpers
import { HandleModalContext } from '../../layout/mainlayout/MainLayout'
import { getFloatPrice, convertPrice } from '../../helpers/price'
import { generateName } from '../../helpers/helpers'
import { handleImageError } from '../../helpers/handleImageError'
import { getRegistrationEventDataInDb } from '../../helpers/registration-on-event'
import { addSalesTax, getCurrency } from '../../helpers/payment'
import useToasterHelper from '../../helpers/ToasterHelper'

import EventService from '../../services/firebase-service/event/EventService'
import EventRegisteredUsersService from '../../services/firebase-service/event-registered-users/EventRegisteredUsersService'
import UserService from '../../services/firebase-service/users/userService'

// Interfaces
import { IRegistrationFeesInterface } from '../../models/registration-fees/registrationFees.interface'
import { IRegistrationTicketInterface } from '../../models/registration-tickets/registrationTicket.interface'
import { IRegistrationByDayInterface } from '../../models/registrations-by-day/registrationByDay.interface'
import { IUserInterface } from '../../models/users/user.interface'
import { IEventInterface } from '../../models/events/event.interface'

// Constants
import { FEES_CATEGORY_CONST } from '../events/views/details/EventDetailsViewComponentFees'
import { MODAL_CONSTS } from '../../const/modal-const'
import { PAYMENT_TYPE } from '../../types/payment'

interface IGroupedEntries extends IRegistrationByDayInterface {
  children: IRegistrationByDayInterface[]
  horses: string[]
  total: number
}
interface IData {
  entries: {
    total: number
    count: number
    items: IRegistrationByDayInterface[]
    groupedItems: IGroupedEntries[]
  }
  fees: { total: number; count: number; items: IRegistrationFeesInterface[] }
  mandatory: { total: number; count: number; items: IRegistrationFeesInterface[] }
  tickets: { total: number; count: number; items: IRegistrationTicketInterface[] }
  salesTax: number
}

interface ScratchFullEntryProps {
  dataToPassOn: {
    eventId: string
    userId: string
    registrationId: string
    isChargeSalesTax: boolean
    isRegistrar: boolean
    onSave?: () => Promise<void>
  }
}

export const ScratchFullEntryModal: React.FC<ScratchFullEntryProps> = ({ dataToPassOn }) => {
  const handleModalContext = useContext(HandleModalContext)

  const toastFunctions = useToasterHelper()

  const [data, setData] = useState<IData>({
    entries: { total: 0, count: 0, items: [], groupedItems: [] },
    fees: { total: 0, count: 0, items: [] },
    mandatory: { total: 0, count: 0, items: [] },
    tickets: { total: 0, count: 0, items: [] },
    salesTax: 0,
  })
  const [totalPrice, setTotalPrice] = useState(0)
  const [exhibitor, setExhibitor] = useState<IUserInterface>()
  const [loading, setLoading] = useState(false)
  const [eventData, setEventData] = useState<IEventInterface | null>(null)

  const currency = getCurrency(eventData?.currency)

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true)

        const eventRegisteredUser = await EventRegisteredUsersService.getById(
          dataToPassOn?.registrationId
        )

        if (!eventRegisteredUser?.userId) {
          toastFunctions.error({
            message: 'Registered user not found',
          })
          return
        }

        const user = await UserService.getUserById(eventRegisteredUser.userId)

        let { registrationsByDay, registrationsFees, registrationsTickets } =
          await getRegistrationEventDataInDb({
            registrationId: dataToPassOn.registrationId,
            ownerId: dataToPassOn.isRegistrar ? null : dataToPassOn.userId,
            type: ['classes', 'fees', 'tickets'],
          })

        const riders = registrationsByDay.filter((rider) => !rider.isDivision)
        let salesTax = 0

        let countEntries = 0
        let totalEntries = 0

        let itemsEntries: IRegistrationByDayInterface[] = []
        riders.forEach((entry) => {
          if (
            !entry.isScratched &&
            !entry.refundAmountAdded &&
            entry.paymentType !== PAYMENT_TYPE.DEFAULT
          ) {
            countEntries += 1
            itemsEntries.push(entry)
            let price = 0
            if (entry.registrationPrice) price += convertPrice(entry.registrationPrice)
            if (entry.isQualify) price += convertPrice(entry?.qualifyFee)
            totalEntries += price
            salesTax += addSalesTax(
              entry.created,
              price,
              entry.includeSalesTax,
              entry.includeSalesTax
            )
          }
        })

        const groupedRegistrationsByDay = itemsEntries.reduce<Record<string, IGroupedEntries>>(
          (acc, item) => {
            const horses: string[] = []
            let total = 0
            riders.forEach((entry) => {
              if (entry.riderId === item.riderId && entry.horsesSelected?.[0]) {
                horses.push(...entry.horsesSelected.map(({ id }) => id))
                if (item.registrationPrice) total += convertPrice(item.registrationPrice)
                if (item.isQualify) total += convertPrice(item?.qualifyFee)
              }
            })

            if (item.riderId) {
              if (!acc[item.riderId]) {
                acc[item.riderId] = {
                  ...item,
                  children: [],
                  horses: uniq(horses),
                  total: total / 100,
                }
              }
              acc[item.riderId].children.push(item)
            }
            return { ...acc }
          },
          {}
        )

        let totalFees = 0
        let countFees = 0
        let itemsFees: IRegistrationFeesInterface[] = []
        let totalGovernance = 0
        let countGovernance = 0
        let itemsGovernance: IRegistrationFeesInterface[] = []
        registrationsFees.forEach((fee) => {
          if (
            !fee.isScratched &&
            !fee.refundAmountAdded &&
            fee.paymentType !== PAYMENT_TYPE.DEFAULT
          ) {
            const price = convertPrice(fee.feesPrice) * fee.selectedUnitsCount
            salesTax += price
            if (fee.feesItemCategory === FEES_CATEGORY_CONST.GOVERNANCE) {
              countGovernance += 1
              itemsGovernance.push(fee)
              totalGovernance += price
            } else {
              countFees += 1
              itemsFees.push(fee)
              salesTax += addSalesTax(fee.created, price, fee.includeSalesTax, fee.includeSalesTax)
            }
          }
        })

        let totalTickets = 0
        let countTickets = 0
        let itemsTickets: IRegistrationTicketInterface[] = []
        registrationsTickets.forEach((ticket) => {
          if (
            !ticket.isScratched &&
            !ticket.refundAmountAdded &&
            ticket.paymentType !== PAYMENT_TYPE.DEFAULT
          ) {
            countTickets += 1
            itemsTickets.push(ticket)
            const price = convertPrice(ticket.ticketPrice) * ticket.selectedUnitsCount
            totalTickets += price
            salesTax += addSalesTax(
              ticket.created,
              price,
              ticket.includeSalesTax,
              ticket.includeSalesTax
            )
          }
        })

        const paidTotal = totalEntries + totalFees + totalGovernance + totalTickets
        setTotalPrice((paidTotal + salesTax) / 100)
        setData({
          entries: {
            total: totalEntries / 100,
            count: countEntries,
            items: itemsEntries,
            groupedItems: Object.values(groupedRegistrationsByDay),
          },
          fees: { total: totalFees / 100, count: countFees, items: itemsFees },
          mandatory: {
            total: totalGovernance / 100,
            count: countGovernance,
            items: itemsGovernance,
          },
          tickets: { total: totalTickets / 100, count: countTickets, items: itemsTickets },
          salesTax: salesTax / 100,
        })

        const eventDataInDb = await EventService.get({ docId: dataToPassOn.eventId })
        setEventData(eventDataInDb.event)
        setExhibitor(user)
      } catch (error) {
        console.error(error, 'error')
      } finally {
        setLoading(false)
      }
    }
    getData().then()
  }, [
    dataToPassOn.eventId,
    dataToPassOn.isChargeSalesTax,
    dataToPassOn.registrationId,
    dataToPassOn.userId,
  ])

  const handleNextClick = () => {
    handleModalContext?.handleModal?.(false, MODAL_CONSTS.SCRATCH_FULL_ENTRY)
    handleModalContext?.handleModal?.(true, MODAL_CONSTS.CONFIRM_SCRATCH_FULL_ENTRY, {
      ...handleModalContext?.dataToPassOn,
      exhibitor,
      registrationDocId: dataToPassOn.registrationId,
      totalCost: totalPrice,
      registrations: data.entries.items,
      fees: [...data.fees.items, ...data.mandatory.items],
      tickets: data.tickets.items,
      isChargeSalesTax: dataToPassOn.isChargeSalesTax,
      eventId: dataToPassOn.eventId,
      onSave: dataToPassOn.onSave,
    })
  }

  const entriesData = React.useMemo(() => {
    return data.entries.groupedItems
      .map((entry) => {
        return {
          icon: (
            <img
              src={entry?.riderProfilePicture || ''}
              alt="icons"
              className="object-cover w-[45px] h-[45px] rounded-[6px]"
              onError={(e) => handleImageError(e, generateName(entry?.riderName || ''))}
            />
          ),
          name: entry?.riderName || 'N/A',
          details: `${entry.children?.length} class${entry.children?.length === 1 ? '' : 'es'} • ${entry.horses.length || 0} horse${entry.horses.length === 1 ? '' : 's'} • ${getFloatPrice(`${currency}${entry?.total}`)}`,
        }
      })
      .filter(Boolean)
  }, [currency, data.entries.groupedItems])

  const otherEntries = []

  if (data.fees.count > 0) {
    otherEntries.push({
      icon: <IconTeam />,
      name: 'Fees',
      details: `${data.fees.count} fees • ${getFloatPrice(`${currency}${data.fees.total}`)}`,
    })
  }
  if (data.mandatory.count > 0) {
    otherEntries.push({
      icon: <IconTeam />,
      name: 'Mandatory fees',
      details: `${data.mandatory.count} fees • ${getFloatPrice(`${currency}${data.mandatory.total}`)}`,
    })
  }
  if (data.tickets.count > 0) {
    otherEntries.push({
      icon: <IconTeam />,
      name: 'Tickets',
      details: `${data.tickets.count} tickets • ${getFloatPrice(`${currency}${data.tickets.total}`)}`,
    })
  }

  if (data.salesTax > 0) {
    otherEntries.push({
      icon: <IconTeam />,
      name: 'Sales Tax',
      details: getFloatPrice(`${currency}${data.salesTax}`),
    })
  }

  return (
    <MainModal
      customTitle={
        <div className={`text-SeabiscuitDark200ThemeColor text-[25px] font-bold`}>
          Scratch full entry
        </div>
      }
      size={'lg'}
      show={true}
      onHide={() => handleModalContext?.handleModal?.(false, MODAL_CONSTS.SCRATCH_FULL_ENTRY)}
      type="SCRATCH_FULL_ENTRY"
      buttons={[
        {
          label: 'Next >',
          loading: false,
          disabled:
            data.entries.count === 0 &&
            data.fees.count === 0 &&
            data.mandatory.count === 0 &&
            data.tickets.count === 0,
          className: '!w-full',
          bgClass: '!bg-SeabiscuitMainThemeColor h-[58px]',
          onClick: handleNextClick,
          textClass: '!text-white uppercase',
          onHoverClass: 'hover:!bg-SeabiscuitMainThemeColorDark',
        },
        {
          label: 'CANCEL',
          loading: false,
          disabled: false,
          className: '!w-full',
          bgClass: '!bg-SeabiscuitIconThemeColor/10 border-0 !mt-2 h-[58px]',
          onClick: () => handleModalContext?.handleModal?.(false, MODAL_CONSTS.SCRATCH_FULL_ENTRY),
          textClass: '!text-[#8597b3] uppercase',
          onHoverClass: 'hover:!bg-SeabiscuitIconThemeColor/20',
        },
      ]}
      className="z-[50]"
      overlayClassName="z-[49]"
    >
      {!loading && exhibitor?.id ? (
        <div
          className={
            'flex flex-1 h-full flex-col gap-[22px] pt-5 w-full text-SeabiscuitDark200ThemeColor'
          }
        >
          <ScratchModalHeader
            exhibitor={exhibitor}
            totalEntries={data.entries.count}
            totalCost={totalPrice}
            currency={currency}
          />
          {entriesData.length > 0 && <EntriesSection title="Entries" entries={entriesData as []} />}
          {data.entries.count === 0 &&
          data.fees.count === 0 &&
          data.mandatory.count === 0 &&
          data.tickets.count === 0 ? (
            <div className={'flex justify-center items-center w-full flex-col flex-1 text-center'}>
              <h2 className={'text-SeabiscuitGray200ThemeColor'}>All entries are scratched</h2>
            </div>
          ) : (
            <EntriesSection title="Other" entries={otherEntries} />
          )}
        </div>
      ) : (
        <div className="w-full  p-4 flex justify-center items-center">
          <ViewsLoader />
        </div>
      )}
    </MainModal>
  )
}
